import { useState } from "react";
import { useThreadRuntime } from "@assistant-ui/react";
import { LoaderIcon, StarIcon } from "lucide-react";

import type { CsatRatingPayload } from "@/lib/api";
import { useT } from "@/locale-context";
import { cn } from "@/lib/utils";

const STAR_COUNT = 5;
const STARS = [1, 2, 3, 4, 5] as const;

export function CsatRating({ data }: { data: CsatRatingPayload }) {
  const runtime = useThreadRuntime({ optional: true });
  const [picked, setPicked] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  const handlePick = async (rating: number) => {
    if (submitted || pending) return;
    if (!runtime) {
      setError(t("slotPicker.sendError"));
      return;
    }
    setError(null);
    setPicked(rating);
    setPending(true);
    try {
      await runtime.append({
        role: "user",
        content: [{ type: "text", text: `\u2192 CSAT rating: ${rating}` }],
      });
      setSubmitted(true);
    } catch (err) {
      console.error("[CsatRating] append failed", err);
      setPicked(null);
      setError(t("slotPicker.retryError"));
    } finally {
      setPending(false);
    }
  };

  // Fill preview: once picked, lock to the picked rating. Otherwise echo the
  // hovered star (also driven by keyboard focus) so the user sees "clicking
  // this star fills 1..N" before committing.
  const fillCount = picked ?? hover ?? 0;

  return (
    <div lang={data.language} className="my-3 flex flex-col gap-3">
      <div className="flex flex-col gap-1 leading-[1.3]">
        <p className="text-[14px] font-medium text-[#353a47]">
          {t("csatRating.label")}
        </p>
        <p className="text-[12px] text-[#353a47]/60">{t("csatRating.hint")}</p>
      </div>

      <div
        role="radiogroup"
        aria-label={t("csatRating.label")}
        className="flex items-center gap-1"
        onMouseLeave={() => setHover(null)}
      >
        {STARS.map((rating) => {
          const isFilled = rating <= fillCount;
          const isPicked = picked === rating;
          const isDisabled = submitted || pending;
          const showLoader = pending && isPicked;

          return (
            <button
              key={rating}
              type="button"
              role="radio"
              aria-checked={picked === rating}
              aria-label={t("csatRating.aria.rate", { n: rating })}
              disabled={isDisabled}
              onClick={() => handlePick(rating)}
              onMouseEnter={() => !isDisabled && setHover(rating)}
              onFocus={() => !isDisabled && setHover(rating)}
              onBlur={() => setHover(null)}
              className={cn(
                "relative inline-flex size-11 items-center justify-center rounded-full transition",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00c0e8]",
                isDisabled ? "cursor-default" : "cursor-pointer hover:bg-[#fffbea]",
              )}
            >
              {showLoader ? (
                <LoaderIcon className="size-6 animate-spin text-[#f59e0b]" />
              ) : (
                <StarIcon
                  className={cn(
                    "size-7 transition-colors",
                    isFilled
                      ? "fill-[#f59e0b] text-[#f59e0b]"
                      : "fill-none text-[#d3dfe7]",
                    !isFilled && !isDisabled && "group-hover:text-[#f59e0b]",
                  )}
                  strokeWidth={1.75}
                />
              )}
            </button>
          );
        })}
        <span className="sr-only">
          {fillCount > 0
            ? `${fillCount}/${STAR_COUNT}`
            : t("csatRating.label")}
        </span>
      </div>

      {submitted ? (
        <p className="text-[12px] text-[#353a47]/70" role="status">
          {t("csatRating.submitted")}
        </p>
      ) : null}

      {error ? (
        <p className="text-[12px] text-rose-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
